<template lang="pug">
  .yz-upload-button
    yz-btn(v-bind="buttonProps" @click="handleButtonClick")
    input.yz-upload-input(ref="fileInput" type="file" :accept="accept" :multiple="multiple" @change="onFileChange")
</template>

<script>
import { resizeImage, isFileSizeOverLimit } from '@/utils'
import { mapActions } from 'vuex'

/**
 * YzUploadButton - A flexible upload button component with dual upload strategies
 *
 * Upload Modes:
 * - 'preview' (default): Files are processed locally, creates blob URLs for previews
 * - 'immediate': Files are uploaded immediately to API endpoint, has 'hid' field in response
 *
 * Databin Integration:
 * When uploadMode is 'immediate', the component will:
 * 1. Upload all files to uploadUrl endpoint in parallel
 * 2. Wait for all uploads to complete using Promise.allSettled()
 * 3. Emit @upload-batch-complete event with successful upload responses
 * 4. Use YzDatabinSync component externally to handle databin synchronization
 *
 * Usage:
 * Preview Mode:
 *   <yz-upload-button v-model="files" label="Upload Files" />
 *
 * Immediate Upload Mode:
 *   <yz-upload-button
 *     v-model="uploadedFiles"
 *     label="Upload to Server"
 *     uploadMode="immediate"
 *     uploadUrl="/documents/"
 *     @upload-success="handleSuccess"
 *   />
 *
 * Immediate Upload with Databin:
 *   <template>
 *     <yz-databin-sync
 *       v-model="databinItems"
 *       ref="databinSync"
 *       @databin-created="handleDatabinCreated"
 *     />
 *     <yz-upload-button
 *       v-model="uploadedFiles"
 *       label="Upload to Server"
 *       uploadMode="immediate"
 *       uploadUrl="/documents/"
 *       @upload-success="handleSuccess"
 *       @upload-batch-complete="handleBatchComplete"
 *     />
 *   </template>
 *
 *   methods: {
 *     handleBatchComplete(uploadResults) {
 *       this.$refs.databinSync.addItems(uploadResults)
 *     }
 *   }
 *
 * Events:
 * - @upload-start: When upload begins
 * - @upload-progress: Progress updates during upload
 * - @upload-success: Upload completed successfully
 * - @upload-error: Upload failed
 * - @upload-batch-complete: Batch upload completed (only in immediate mode)
 */
export default {
  name: 'YzUploadButton',
  props: {
    // v-model support
    value: {
      type: Array,
      default: () => []
    },
    // File upload specific props
    multiple: {
      type: Boolean,
      default: false
    },
    accept: {
      type: String,
      default: 'image/*'
    },
    appendFiles: {
      type: Boolean,
      default: false
    },
    // Upload strategy props
    uploadMode: {
      type: String,
      default: 'preview',
      validator: value => ['immediate', 'preview'].includes(value)
    },
    uploadUrl: {
      type: String,
      default: '/documents/'
    },
    uploadHeaders: {
      type: Object,
      default: () => ({})
    },
    uploadMethod: {
      type: String,
      default: 'POST'
    },
    // File size limit props
    fileSizeLimit: {
      type: Number,
      default: 5 // Default 5MB limit
    },
    // YzBtn props
    label: {
      type: String,
      default: 'Upload'
    },
    size: {
      type: String,
      default: 'md'
    },
    color: {
      type: String,
      default: 'primary'
    },
    textColor: {
      type: String,
      default: null
    },
    backgroundColor: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    block: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    outlined: {
      type: Boolean,
      default: false
    },
    height: {
      type: String,
      default: null
    },
    width: {
      type: String,
      default: null
    },
    appendIcon: {
      type: String,
      default: null
    },
    prependIcon: {
      type: String,
      default: null
    },
    icon: {
      type: String,
      default: null
    },
    iconSize: {
      default: 24
    },
    iconColor: {
      type: String,
      default: null
    },
    dark: {
      type: Boolean,
      default: false
    },
    underline: {
      type: Boolean,
      default: false
    },
    text: {
      type: Boolean,
      default: false
    },
    buttonClass: {
      type: String,
      default: null
    },
    unsetMinWidth: {
      type: Boolean,
      default: false
    },
    iconOnly: {
      type: Boolean,
      default: false
    },
    secondary: {
      type: Boolean,
      default: false
    },
    disableRipple: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      uploadingFiles: new Map()
    }
  },
  computed: {
    buttonProps() {
      const props = { ...this.$props }
      // Remove non-YzBtn props
      delete props.value
      delete props.multiple
      delete props.accept
      delete props.appendFiles
      delete props.uploadMode
      delete props.uploadUrl
      delete props.uploadHeaders
      delete props.uploadMethod
      delete props.fileSizeLimit
      return props
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    handleButtonClick() {
      this.$refs.fileInput.click()
    },
    async onFileChange(e) {
      const files = e.target.files || e.dataTransfer.files
      if (!files.length) {
        return
      }
      const processedFiles = []
      const uploadPromises = []

      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i]
          const fileId = `file_${Date.now()}_${i}`

          // Check file size limit
          if (isFileSizeOverLimit(file, this.fileSizeLimit)) {
            await this.showMessage(['error', `${file.name}: ${this.$t('error_message.file_reached_limit')}`])
            continue // Skip this file and continue with others
          }
          // Handle upload based on mode
          if (this.uploadMode === 'immediate') {

            let fileToUpload = file

            // For image files, apply resizing
            if (file.type.startsWith('image/')) {
              try {
                const resizedImage = await resizeImage(file, file.name)
                fileToUpload = resizedImage.file
              } catch (error) {
                console.error('Failed to resize image:', error)
                await this.showMessage(['error', `${file.name}: error`])
                continue // Skip this file and continue with others
              }
            }

            // Immediate upload mode - create file object first, then upload
            const fileObj = {
              fileId,
              file: fileToUpload,
              filename: fileToUpload.name,
              size: fileToUpload.size,
              type: fileToUpload.type,
              uploaded: false,
              uploading: true,
              progress: 0,
              error: null
            }
            // Start upload in background and update the file object
            const uploadPromise = this.uploadToApi(fileToUpload, fileId, fileObj)
              .then(uploadResponse => {
                // Update file object with upload response
                Object.assign(fileObj, {
                  uploaded: true,
                  uploading: false,
                  progress: 100,
                  ...uploadResponse
                })
                return { success: true, fileId, file: fileToUpload, response: uploadResponse }
              })
              .catch(error => {
                Object.assign(fileObj, {
                  uploaded: false,
                  uploading: false,
                  progress: 0,
                  error: error
                })
                return { success: false, fileId, file: fileToUpload, error }
              })

            // Store upload promise for batch processing
            processedFiles.push(fileObj)
            uploadPromises.push(uploadPromise)
          } else {
            // Preview mode - create blob URLs (current behavior)
            if (file.type.startsWith('image/')) {
              try {
                const resizedImage = await resizeImage(file, file.name)
                processedFiles.push({
                  fileId,
                  blob: resizedImage.dataURL,
                  file: resizedImage.file,
                  filename: file.name,
                  size: file.size,
                  type: file.type,
                  uploaded: false,
                  uploading: false,
                  progress: 0,
                  error: null
                })
              } catch (error) {
                console.error('Failed to resize image:', error)
                await this.showMessage(['error', `${file.name}: error`])
                continue // Skip this file on error
              }
            } else {
              // For non-image files, don't create a blob preview
              processedFiles.push({
                fileId,
                blob: null,
                file: file,
                filename: file.name,
                size: file.size,
                type: file.type,
                uploaded: false,
                uploading: false,
                progress: 0,
                error: null
              })
            }
          }

          // If not multiple, break after first file
          if (!this.multiple) {
            break
          }
        }

        // Handle batch upload completion for immediate mode
        if (this.uploadMode === 'immediate' && uploadPromises.length > 0) {
          // Wait for all uploads to complete
          Promise.allSettled(uploadPromises)
            .then(results => {
              // Extract successful upload responses
              const successfulUploads = results
                .filter(result => result.status === 'fulfilled' && result.value.success)
                .map(result => result.value.response)

              if (successfulUploads.length > 0) {
                this.$emit('upload-batch-complete', successfulUploads)
              }
            })
        }

        // Handle file output based on mode
        let result
        if (this.multiple) {
          if (this.appendFiles && this.value && this.value.length > 0) {
            // Append new files to existing ones
            result = [...this.value, ...processedFiles]
          } else {
            // Replace with new files
            result = processedFiles
          }
        } else {
          // For single file mode, always return array of length 1
          result = [processedFiles[0]]
        }
        this.$emit('input', result)

        // Clear the input to allow re-selecting the same file
        e.target.value = ''
      } catch (error) {
        console.error('Error processing files:', error)
        this.$emit('error', error)
      }
    },
    async uploadToApi(file, fileId, fileObj = null) {
      if (!file) {
        return Promise.reject(new Error('File is required for upload'))
      }

      return new Promise((resolve, reject) => {
        // Create FormData for the file
        const formData = new FormData()
        formData.append('file', file)

        // Initialize upload tracking
        this.uploadingFiles.set(fileId, { progress: 0, uploading: true })
        this.$emit('upload-start', { file, fileId })

        const headers = {
          'Content-Type': 'multipart/form-data',
          ...this.uploadHeaders
        }

        this.$axios({
          method: this.uploadMethod,
          url: this.uploadUrl,
          data: formData,
          headers,
          onUploadProgress: progressEvent => {
            let progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            if (progress === 100) {
              // Let the 'then' handler set to 100% to avoid premature completion
              progress = 96
            }
            this.uploadingFiles.set(fileId, { progress, uploading: true })

            // Update the file object directly if provided
            if (fileObj) {
              fileObj.progress = progress
            }

            this.$emit('upload-progress', { file, fileId, progress })
          }
        })
          .then(response => {
            // File uploaded to primary endpoint
            const uploadResponse = response.data
            this.uploadingFiles.set(fileId, { progress: 100, uploading: false, completed: true })
            this.$emit('upload-success', { file, fileId, response: uploadResponse })
            resolve(uploadResponse)
          })
          .catch(error => {
            this.uploadingFiles.set(fileId, { progress: 0, uploading: false, error: true })
            this.$emit('upload-error', { file, fileId, error })
            reject(error)
          })
      })
    }
  }
}
</script>

<style scoped>
.yz-upload-button {
  position: relative;
  display: inline-block;
}

.yz-upload-input {
  position: absolute;
  left: -9999px;
  opacity: 0;
  pointer-events: none;
}
</style>
